// ABOUTME: React Query mutation hooks for creating, updating, and deleting load spacing configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  LoadSpacingApiResponse,
  LoadSpacingFormValues,
  UpsertLoadSpacingRequest,
} from '../types/loadSpacing'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

export const toApiRequest = (values: LoadSpacingFormValues): UpsertLoadSpacingRequest => ({
  allowStockpiling: values.allowStockpiling,
  timeOnJobSite: values.timeOnJobSite,
  spacing: values.spacing,
  flowRate: values.flowRate,
})

export const useCreateLoadSpacing = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: LoadSpacingFormValues }) => {
      const res = await api.post<LoadSpacingApiResponse>(
        `/tuc-configs/load-spacing/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loadSpacingConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'loadSpacing', countryId, businessLine] })
    },
  })
}

export const useCreateLoadSpacingWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values }: { scope: PendingScopeRequest; values: LoadSpacingFormValues }) => {
      const res = await api.post<LoadSpacingApiResponse>(
        '/tuc-configs/load-spacing',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values)),
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loadSpacingConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'loadSpacing', countryId, businessLine] })
    },
  })
}

export const useUpdateLoadSpacing = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: LoadSpacingFormValues }) => {
      const res = await api.put<LoadSpacingApiResponse>(
        `/tuc-configs/load-spacing/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loadSpacingConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'loadSpacing', countryId, businessLine] })
    },
  })
}

export const useDeleteLoadSpacing = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/load-spacing/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loadSpacingConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'loadSpacing', countryId, businessLine] })
    },
  })
}
